import React from 'react';
import { useAppContext } from 'src/components/app-context';
import { ProductCollection, DescriptionSlice } from '../../../../types';
import * as S from './styles';

type Props = {
  productCollection: ProductCollection;
} & Pick<DescriptionSlice, 'description'>;

const Description = ({ productCollection, description }: Props) => {
  const { t, linkTo } = useAppContext();

  const scrollToTop = React.useCallback(() => window.scroll(0, 0), []);
  const aliases: React.ReactNode = React.useMemo(() => {
    const categoryUrl = linkTo('productCollection', {
      pathName: productCollection.url,
      filters: productCollection.filters,
    });

    const arr =
      productCollection.aliases?.filter(
        // We don't want empty aliases
        alias => alias.trim()
      ) ?? [];
    const links = arr.map((alias, index) => (
      <React.Fragment key={alias}>
        <S.AliasLink to={categoryUrl} onClick={scrollToTop}>
          {alias}
        </S.AliasLink>
        {arr.length > 2 && index < arr.length - 2 && <>, </>}
      </React.Fragment>
    ));

    if (links.length === 1) {
      return t('category', 'product-collection.aliases-single', {
        alias: links[0],
      });
    }

    if (links.length > 1) {
      return t('category', 'product-collection.aliases-plural', {
        aliasesExceptLast: links.slice(0, links.length - 1),
        last: links[links.length - 1],
      });
    }

    return null;
  }, [productCollection, linkTo, t]);

  if (!description && !aliases) return null;

  return (
    <S.DescriptionContainer>
      <S.DescriptionTitle>{productCollection.name}</S.DescriptionTitle>
      <S.Description data-test="ProductCollectionDescription">
        {description}
        {description && aliases && <> </>}
        {aliases}
      </S.Description>
    </S.DescriptionContainer>
  );
};

export default Description;
